import $ from 'jquery';
import AjaxForm from './ajax-form';

const ProfileForm = function ($form) {
    const profileForm = this;

    AjaxForm.call(this, $form);

    profileForm.slideDuration = 200;
};

ProfileForm.prototype = Object.create(AjaxForm.prototype);

ProfileForm.prototype.bindInputs = function ($inputs) {
    const profileForm = this;

    AjaxForm.prototype.bindInputs.call(this, $inputs);

    // Initialize select2 components
    $inputs.filter('.profile-form-select2').each(function () {
        const $select = $(this);

        $select.select2(profileForm.makeSelect2Parameters($select));
    });
};

ProfileForm.prototype.handleAction = function ($button) {
    const profileForm = this;
    const method = $button.data('method') ?? 'POST';

    let $visualButton = $button;
    if ($button.data('bsDismiss') === 'modal') {
        $visualButton = $button.closest('.modal').data('$button') ?? $button;
    }

    profileForm.showInputsAsPending($visualButton);

    $.ajax({
        url: $button.attr('formaction'),
        method: 'POST',
        data: method === 'POST' ? null : {_method: method},
        dataType: 'json',
    })
        .then(
            response => profileForm.handleAjaxResponse(response),
            xhr => profileForm.handleAjaxFailure(xhr),
        )
        .then(() => profileForm.showInputsAsDone($visualButton));
};

ProfileForm.prototype.query = function ($element, data) {
    const profileForm = this;
    const url = profileForm.$form[0].action + '/' + $element[0].name;

    return $.ajax({
        url: url,
        type: 'GET',
        data: data,
    });
};

ProfileForm.prototype.handleAjaxResponse = function (response) {
    const profileForm = this;
    const promises = [];

    for (const error of response.errors ?? []) {
        if (error.element) {
            promises.push(profileForm.handleErrors($(profileForm.$form[0][error.element]), error.messages));
        } else {
            promises.push(profileForm.handleGlobalErrors(error.messages));
        }
    }

    for (const success of response.successes ?? []) {
        promises.push(profileForm.handleSuccess($(profileForm.$form[0][success.element])));
    }

    for (const added of response.added ?? []) {
        promises.push(profileForm.handleAdded($(profileForm.$form[0][added.element]), added.children));
    }

    for (const removed of response.removed ?? []) {
        promises.push(profileForm.handleRemoved($(profileForm.$form[0][removed.element])));
    }

    for (const changed of response.changed ?? []) {
        promises.push(profileForm.handleChanged($(profileForm.$form[0][changed.element]), changed.disabled, changed.data));
    }

    return $.when(...promises);
};

ProfileForm.prototype.handleAjaxFailure = function (xhr) {
    const profileForm = this;

    if (xhr.status === 0) {
        return null;
    }

    const message = xhr.responseJSON?.message
        || xhr.responseJSON?.exception
        || (xhr.status + ' ' + xhr.statusText);

    return profileForm.handleGlobalErrors([message]);
}

ProfileForm.prototype.handleErrors = function ($element, messages) {
    const profileForm = this;

    profileForm.showInputsAsInvalid($element, messages);

    return null;
};

ProfileForm.prototype.handleGlobalErrors = function (messages) {
    for (const message of messages) {
        console.error(message);
        notyf.error({message: message, duration: 0, dismissible: true});
    }

    return null;
};

ProfileForm.prototype.handleSuccess = function ($element) {
    const profileForm = this;

    profileForm.showInputsAsValid($element);

    return null;
};

ProfileForm.prototype.handleAdded = function ($element, childrenHtml) {
    const profileForm = this;

    for (const childHtml of childrenHtml) {
        // Insert element in DOM
        const $child = $(childHtml).hide().insertBefore($element.children('.profile-relation-add'));

        // Initialize element
        profileForm.initElement($child);

        // Show element with a "slide down" animation
        $child.slideDown(profileForm.slideDuration)

        // Then focus the first input in element and scroll it into view
        $child.queue(function (next) {
            $child.find(':input[name]:not(button)').first().trigger('focus');
            $child[0].scrollIntoView({block: 'nearest'});
            next();
        });
    }

    return null;
};

ProfileForm.prototype.handleRemoved = function ($element) {
    const profileForm = this;

    // Prepare element for DOM removal
    profileForm.prepareElementForRemoval($element);

    // Hide element with a "slide up" animation
    $element.slideUp(profileForm.slideDuration)

    // Then remove item from DOM
    $element.queue(function (next) {
        $element.remove();
        next();
    });

    return null;
};

ProfileForm.prototype.handleChanged = function ($element, disabled, data) {
    const profileForm = this;
    const promises = [];

    if (disabled !== undefined) {
        $element
            .prop('disabled', disabled)
            .trigger('changeDisabled.sysVision', disabled);

        if ($element.is('.profile-relation-add')) {
            $element.parent().toggleClass('adding-disabled', disabled);
        } else if ($element.is('.form-field-input')) {
            const $field = $element.closest('.form-field');

            $field.toggleClass('disabled', disabled);

            if ($field.is('.hidden-when-disabled')) {
                if (disabled) {
                    $field.slideUp(profileForm.slideDuration);
                } else {
                    $field.slideDown(profileForm.slideDuration);
                }
            }
        }
    }

    if (data !== undefined) {
        profileForm.abortInputs($element);

        if ($element.is('.profile-form-select2')) {
            promises.push(profileForm.handleChangedSelect2($element, data));
        } else if ($element.is('.cercle-soin-editor')) {
            promises.push(profileForm.handleChangedCercleSoinEditor($element, data));
        } else if ('value' in data) {
            if ($element.is(':checkbox')) {
                $element.prop('checked', data.value);
            } else {
                $element.val(data.value);
            }
        }
    }

    return $.when(...promises);
};


ProfileForm.prototype.initElement = function ($element) {
    const profileForm = this;

    // Initialize element's date picker components
    $element.datepickerContainerInit();

    // Bind element inputs
    profileForm.bindInputs($element.find(':input[name]'));

    // Initialize element's tooltips
    $element.find('[data-bs-toggle=tooltip]').tooltip();
};

ProfileForm.prototype.prepareElementForRemoval = function ($element) {
    // Destroy all element's tooltips
    $element.find('[data-bs-toggle=tooltip]').tooltip('dispose');

    // Destroy all element's select2 components
    $element.find('.profile-form-select2').select2('destroy');

    // Remove element's "remove" button
    $element.children('.profile-relation-remove').remove();
};

ProfileForm.prototype.makeSelect2Parameters = function ($select) {
    const profileForm = this;

    return {
        ajax: {
            data: data => data,
            transport: function (params, success, failure) {
                // A select2 query triggered by ProfileForm API may already contain a pre-received response from server.
                const preReceivedResponse = params.data.response;
                if (preReceivedResponse) {
                    delete params.data.response;
                    success(preReceivedResponse);

                    return;
                }

                // Prepare data
                const data = {};
                for (const key of ['term', 'page']) {
                    if (key in params.data) {
                        data[key] = params.data[key];
                    }
                }

                // Send query via ProfileForm API
                return profileForm.query($select, data).done(success).fail(failure);
            },
        },
    };
};

ProfileForm.prototype.handleChangedSelect2 = function ($select, data) {
    if (data.select2) {
        const select2 = $select.data('select2');

        if (data.value === null) {
            // Close other opened select2 components, if any.
            $('.select2.select2-container--open').siblings('select').not($select).select2('close');

            // Open select2 component with provided options
            select2.trigger('query', {response: data.select2});
        } else {
            $select.empty();

            const selectedOptionData = data.select2.results.find(opt => opt.id === data.value);

            if (selectedOptionData) {
                select2.dataAdapter.option(selectedOptionData).appendTo($select);
            }
        }
    }

    $select.val(data.value).trigger('change.select2');

    return null;
};

ProfileForm.prototype.handleChangedCercleSoinEditor = function ($container, data) {
    const editor = $container.cercleSoinEditor();
    const promises = [];

    if (data.reload) {
        promises.push(editor.reloadTable());
    }

    if (data.broadcast) {
        editor.broadcast();
    }

    return $.when(...promises);
};

$.fn.profileForm = function () {
    this.each(function () {
        const $form = $(this);

        if (! $form.data('profileForm')) {
            $form.data('profileForm', new ProfileForm($form));
        }
    });

    return this.data('profileForm');
};

$(function () {
    $('.profile-form').profileForm();
});

$(document).on('click', '.profile-form-action', function () {
    const profileForm = $(this.form).data('profileForm');

    if (profileForm) {
        profileForm.handleAction($(this));
    }
});

export default ProfileForm;
